import { ethers } from "ethers";
import { connectDevice, checkDevice } from "../usb/connection";
import { exportWASM, ETHEREUM_COIN_TYPE } from "../usb/protocol";
import * as protocol from "../usb/protocol";
import { DeviceError } from "../errors";
import { DeviceAccount } from "../types";

/**
 * Parse a BIP-44 path string (e.g. "44'/60'/0'/0/0") into a number array
 * suitable for the WASM layer.
 */
export function splitPath(path: string): number[] {
  const result: number[] = [];
  path.split("/").forEach((element) => {
    let number = parseInt(element, 10);
    if (isNaN(number)) return;
    if (element.endsWith("'")) number += 0x80000000;
    result.push(number);
  });
  return result;
}

/**
 * Derive a checksummed Ethereum address from whatever the Keyfob returns for
 * crypto_guard_if_get_xpub with coin type 60 (Ethereum).
 *
 * Handles three encodings:
 *  - 33 or 65 bytes → raw compressed/uncompressed SEC key
 *  - 111+ bytes     → ASCII-encoded BIP-32 xpub string (null-terminated)
 */
export function xpubBytesToAddress(pubKeyBytes: number[] | Uint8Array): string {
  const bytes = new Uint8Array(pubKeyBytes);

  if (bytes.length === 33 || bytes.length === 65) {
    return ethers.computeAddress(ethers.hexlify(bytes));
  }

  let xpubString = "";
  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] === 0) break;
    xpubString += String.fromCharCode(bytes[i]);
  }
  const hdNode = ethers.HDNodeWallet.fromExtendedKey(xpubString.trim());
  return hdNode.address;
}

/**
 * Fetch the BIP-32 extended public key for `path` from the connected Keyfob.
 * Returns raw bytes as a number array.
 */
export async function getExtPubKey(path: string | number[]): Promise<number[]> {
  const ev = protocol.resetOperation();
  const pathArray = typeof path === "string" ? splitPath(path) : path;
  protocol.pathBuffer.fill(0);
  protocol.pathBuffer.set(new Uint32Array(pathArray));

  await exportWASM?.crypto_guard_if_get_xpub(
    ETHEREUM_COIN_TYPE,
    protocol.pathBuffer.byteOffset,
    pathArray.length,
  );
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new DeviceError("TIMEOUT", "No response from device.")),
      30_000,
    );
    ev.once("ResolveExtPubKey", () => { clearTimeout(timer); resolve(); });
  });

  if (protocol.res.error) throw protocol.res.error;
  return protocol.res.data;
}

/**
 * Derive up to `count` EVM accounts starting at `startIndex` from the
 * connected Keyfob (path m/44'/60'/0'/0/i).
 *
 * Returns address and xpub bytes — balance fetching is the caller's concern.
 */
export async function evmGetAddress(
  startIndex: number = 0,
  count: number = 5,
  onProgress?: (msg: string) => void,
): Promise<DeviceAccount[]> {
  onProgress?.("Please wait while fetching accounts…");
  const accounts: DeviceAccount[] = [];

  for (let i = startIndex; i < startIndex + count; i++) {
    const path = `44'/60'/0'/0/${i}`;
    if (!checkDevice()) await connectDevice();

    const xpub = await getExtPubKey(path);
    const address = xpubBytesToAddress(xpub);
    if (address && address !== "NaN") {
      accounts.push({ path, xpub, address });
    }
  }

  return accounts;
}
